import { useSyncExternalStore } from 'react';
import type { SessionBranch, SessionUser } from '@his/shared';
import { BRANCH_KEY, session } from './api';
import { useAuth } from './auth';
import { adoptSessionDisplay } from './utils';

const listeners = new Set<() => void>();
let selected: string | null = readSelected();
let user: SessionUser | null = null;

function readSelected(): string | null {
  try {
    return session.branch();
  } catch {
    return null;
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribeToBranch(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSelected(): string | null {
  return selected;
}

export function selectedBranchId(): string | null {
  return selected;
}

export function bindBranchUser(next: SessionUser | null): void {
  user = next;
}

export function setSelectedBranch(branchId: string | null): void {
  selected = branchId;
  session.setBranch(branchId);
  adoptSessionDisplay(user, branchId);
  notify();
}

export function followBranchAcrossTabs(target: Pick<Window, 'addEventListener'> = window): void {
  target.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== BRANCH_KEY) return;
    const next = readSelected();
    if (next === selected) return;
    selected = next;
    adoptSessionDisplay(user, next);
    notify();
  });
}

export function useSelectedBranch(): string | null {
  return useSyncExternalStore(subscribeToBranch, getSelected, getSelected);
}

export function workingBranchId(selectedId: string | null, current: SessionUser | null): string {
  if (selectedId && (current?.branches ?? []).some((branch) => branch.id === selectedId)) return selectedId;
  return current?.defaultBranchId ?? current?.branches?.[0]?.id ?? '';
}

export function useWorkingBranchId(): string {
  const { user: current } = useAuth();
  const selectedId = useSelectedBranch();
  return workingBranchId(selectedId, current);
}

export function selectedBranch(current: SessionUser | null, selectedId: string | null): SessionBranch | null {
  if (!selectedId) return null;
  return (current?.branches ?? []).find((branch) => branch.id === selectedId) ?? null;
}

export function useBranchLock(): { branchId: string; locked: boolean } {
  const { user: current } = useAuth();
  const selectedId = useSelectedBranch();
  const locked = Boolean(selectedId && (current?.branches ?? []).some((branch) => branch.id === selectedId));
  return { branchId: locked ? (selectedId as string) : '', locked };
}

export function ensureBranchSelected(current: SessionUser | null): void {
  const branches = current?.branches ?? [];
  if (!current || current.isPlatformUser || branches.length === 0) return;
  if (selected && branches.some((branch) => branch.id === selected)) return;
  const fallback = branches.find((branch) => branch.id === current.defaultBranchId) ?? branches[0];
  selected = fallback.id;
  session.setBranch(fallback.id);
  notify();
}
